import autobind from 'autobind-decorator';
import { h } from 'vue';
import type { Rule } from 'ant-design-vue/es/form';
import { Inject, Injectable, forwardRef } from '@kaokei/use-vue-service';
import {
  loginByCode,
  loginByFeishu,
  loginByPassword,
  loginByVerifyCode,
  loginByWechat,
  sendVerifyCode,
} from '@core/api/login';
import HTProtocol from '@core/components/HTProtocol.vue';
import { StorageService } from '@core/services/storage.service';
import {
  HT_PLATFORM,
  LOGIN_SOURCE,
  TENCENT_TCAPTCHA_APP_ID,
  TENCENT_TCAPTCHA_URL,
} from '@core/utils/constant';
import { encode } from '@core/utils/encode';
import { loadScript } from '@core/utils/loadScript';
import { showConfirmModal } from '@core/utils/promiseModal';
import { showErrorMsg } from '@core/utils/utility';
import { UserService } from './user.service';

@Injectable()
export class LoginService {
  public loginSource = '';
  public verifycodeFormRef: any;
  public passwordFormRef: any;
  public LOGIN_TYPE_VERIFYCODE = 1;
  public LOGIN_TYPE_PASSWORD = 2;
  public loginType = this.LOGIN_TYPE_VERIFYCODE;
  public visible = false;
  public formDisabled = true;
  public loading = false;
  public countdownSecond = 0;
  public countdownTimer = 0;
  // 字段的真实状态
  public validModel: Record<string, boolean> = {
    phoneNumber: false,
    password: false,
    verifyCode: false,
  };
  // 校验不通过的状态值
  public VALIDATE_ERROR = 'warning';
  // 字段的展示状态
  public validModelStatus: Record<string, string> = {
    phoneNumber: '',
    password: '',
    verifyCode: '',
  };
  // 字段的展示状态对应的文案
  public validModelHelp: Record<string, string> = {
    phoneNumber: '',
    password: '',
    verifyCode: '',
  };
  public phoneNumberPattern = '^1\\d{10}$';
  public model = {
    phoneNumber: '',
    verifyCode: '',
    password: '',
    countryCode: '86',
    short: 'CN',
    privacy: false,
  };
  public rules: Record<string, Rule[]> = {
    phoneNumber: [
      { validator: this.validatePhoneNumber, trigger: ['change', 'blur'] },
    ],
    password: [
      { required: true, message: '请输入密码', trigger: ['change', 'blur'] },
    ],
    verifyCode: [
      {
        required: true,
        pattern: /^\d{6}$/,
        message: '验证码为6位数字',
        trigger: ['change', 'blur'],
      },
    ],
  };

  @Inject(forwardRef(() => StorageService))
  public storageService!: StorageService;

  @Inject(forwardRef(() => UserService))
  public userService!: UserService;

  @autobind
  public validatePhoneNumber(rule: any, value: any) {
    if (value) {
      const pattern = new RegExp(this.phoneNumberPattern);
      if (pattern.test(value)) {
        return Promise.resolve();
      } else {
        return Promise.reject('请输入合法手机号');
      }
    } else {
      return Promise.reject('请输入手机号');
    }
  }

  public init() {
    const value = this.storageService.getTCaptureCountdown();
    if (value > 0) {
      this.countdownSecond = value;
      this.countdownTick();
    }
  }

  public showLoginModal() {
    this.visible = true;
  }

  public hideLoginModal() {
    this.visible = false;
  }

  private async checkPrivacy() {
    if (this.model.privacy) {
      return true;
    }
    return showConfirmModal({
      width: 320,
      class: 'footer-center',
      centered: true,
      okText: '同意',
      okButtonProps: {
        style: 'margin-left: 24px',
      },
      content: () => h(HTProtocol),
      icon: () => null,
    }).then((res) => {
      this.model.privacy = res as boolean;
      return res;
    });
  }

  @autobind
  public updateFormStatus() {
    const vm = this.validModel;
    const type = this.loginType;
    if (type === this.LOGIN_TYPE_VERIFYCODE) {
      this.formDisabled = !(vm.phoneNumber && vm.verifyCode);
    } else {
      this.formDisabled = !(vm.phoneNumber && vm.password);
    }
  }

  @autobind
  public handleChangeLoginType() {
    const formRef =
      this.loginType === this.LOGIN_TYPE_VERIFYCODE
        ? this.verifycodeFormRef
        : this.passwordFormRef;
    formRef?.validate().then(() => {
      this.updateFormStatus();
    });
  }

  @autobind
  public handleOnValidate(name: any, status: boolean, err: any) {
    this.validModel[name] = status;
    this.updateFormStatus();
    const isFocus = this.isFocus(name);
    const isEmpty = !(this.model as any)[name];
    if (isFocus || status || isEmpty) {
      this.validModelStatus[name] = '';
      this.validModelHelp[name] = '';

      if (name === 'verifyCode') {
        const code = this.model.verifyCode;
        this.model.verifyCode = code ? code.replace(/[^\d]/g, '') : '';
      }
    } else {
      this.validModelStatus[name] = this.VALIDATE_ERROR;
      this.validModelHelp[name] = err && err[0];
    }
  }

  private isFocus(name: string) {
    const formRef =
      this.loginType === this.LOGIN_TYPE_VERIFYCODE
        ? this.verifycodeFormRef
        : this.passwordFormRef;
    if (formRef && formRef.$el) {
      const id = `#form_item_${name}`;
      const el = formRef.$el.querySelector(id);
      return el === document.activeElement;
    }
    return false;
  }

  public loginByPassword() {
    this.loginSource = LOGIN_SOURCE.ACCOUNT_PASSWORD;
    const params = {
      phoneNumber: encode(this.model.phoneNumber),
      password: encode(this.model.password),
      countryCode: this.model.countryCode,
      short: this.model.short,
    };
    return loginByPassword(params);
  }

  @autobind
  public async handleLoginByPassword() {
    try {
      if (!(await this.checkPrivacy())) return;

      this.loading = true;
      // 调用登录接口-获取token
      const { token } = await this.loginByPassword();
      // 通过token获取用户数据
      await this.userService.initUserByToken(token);
    } catch (e) {
      // 登录失败
      showErrorMsg(e);
    } finally {
      this.loading = false;
    }
  }

  public loginByVerifyCode() {
    this.loginSource = LOGIN_SOURCE.PHONE_VERIFY_CODE_PC;
    const params = {
      phoneNumber: encode(this.model.phoneNumber),
      verifyCode: this.model.verifyCode,
      countryCode: this.model.countryCode,
      short: this.model.short,
    };
    return loginByVerifyCode(params);
  }

  @autobind
  public async handleLoginByVerifyCode() {
    try {
      if (!(await this.checkPrivacy())) return;

      this.loading = true;
      // 调用登录接口-获取token
      const { token } = await this.loginByVerifyCode();
      // 通过token获取用户数据
      await this.userService.initUserByToken(token);
    } catch (e) {
      // 登录失败
      showErrorMsg(e);
    } finally {
      this.loading = false;
    }
  }

  public loginByWechat(code: string, state: string) {
    this.loginSource = LOGIN_SOURCE.WEIXIN_SCAN_QRCODE;
    return loginByWechat({ code, state });
  }

  public loginByCode(code: string) {
    this.loginSource = LOGIN_SOURCE.HETAO_TEMP_CODE;
    return loginByCode({ code });
  }

  public loginByFeishu(token: string, userId: number) {
    this.loginSource = LOGIN_SOURCE.HETAO_STAFF_TOKEN;
    return loginByFeishu(token, userId);
  }

  public sendVerifyCode(ticket: string, randstr: string) {
    return sendVerifyCode({
      checkType: 1,
      platform: HT_PLATFORM,
      clientName: HT_PLATFORM,
      countryCode: this.model.countryCode,
      phoneNumber: encode(this.model.phoneNumber),
      ticket,
      randstr,
    });
  }

  @autobind
  public handleSendVerifyCode() {
    loadScript(TENCENT_TCAPTCHA_URL).then(() => {
      try {
        const captcha = new TencentCaptcha(
          TENCENT_TCAPTCHA_APP_ID,
          this.tencentCaptchaCallback,
          {},
        );
        captcha.show();
      } catch (e) {
        showErrorMsg(e);
      }
    });
  }

  @autobind
  public tencentCaptchaCallback(res: any) {
    if (res.ret === 0) {
      this.sendVerifyCode(res.ticket, res.randstr)
        .then(() => {
          // 完成发送验证码
          this.startCountdown();
        })
        .catch((e) => {
          showErrorMsg(e);
        });
    }
  }

  public countdownTick() {
    if (this.countdownSecond > 0) {
      this.countdownTimer = window.setTimeout(() => {
        this.countdownSecond -= 1;
        this.countdownTick();
      }, 1000);
    } else {
      this.storageService.removeTCaptureCountdown();
    }
  }

  public startCountdown() {
    this.countdownSecond = 60;
    this.storageService.setTCaptureCountdown();
    this.countdownTick();
  }

  public dispose() {
    window.clearTimeout(this.countdownTimer);
  }
}
